import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  LinearProgress,
  Alert,
  IconButton,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import { useSnackbar } from 'notistack';
import projectRoleRequest from '../../../requests/api/project_role';

function BulkUploadRolesModal({ open, onClose, onSuccess }) {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const { enqueueSnackbar } = useSnackbar();

  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile) {
      const fileExtension = selectedFile.name.split('.').pop().toLowerCase();
      if (fileExtension === 'csv') {
        setFile(selectedFile);
        setUploadResult(null);
      } else {
        enqueueSnackbar('Por favor seleccione un archivo CSV válido', {
          variant: 'error',
        });
      }
    }
  };

  const handleUpload = async () => {
    if (!file) {
      enqueueSnackbar('Por favor seleccione un archivo', {
        variant: 'warning',
      });
      return;
    }

    setUploading(true);
    setUploadResult(null);

    try {
      const response = await projectRoleRequest.postBulkUploadProjectRoles(file);

      if (response.success) {
        const created = response.data.data?.created || 0;
        const errors = response.data.data?.errors || [];
        const totalRows = response.data.data?.totalRows || 0;

        setUploadResult({
          success: true,
          message: response.data.data?.message || 'Roles cargados exitosamente',
          created,
          errors,
          totalRows,
        });

        const hasErrors = errors.length > 0;
        enqueueSnackbar(
          hasErrors
            ? `${created} de ${totalRows} roles cargados (${errors.length} errores)`
            : `${created} roles cargados exitosamente`,
          { variant: hasErrors ? 'warning' : 'success' },
        );

        if (onSuccess) {
          onSuccess();
        }
      } else {
        setUploadResult({
          success: false,
          message: response.message || 'Error al cargar los roles',
          errors: response.data?.errors || [],
        });

        enqueueSnackbar(response.message || 'Error al cargar los roles', {
          variant: 'error',
        });
      }
    } catch (error) {
      setUploadResult({
        success: false,
        message: 'Error inesperado al cargar el archivo',
        errors: [],
      });

      enqueueSnackbar('Error inesperado al cargar el archivo', {
        variant: 'error',
      });
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    setFile(null);
    setUploadResult(null);
    onClose();
  };

  const downloadTemplate = () => {
    const csvContent = 'name;minimumLevel\n'
      + 'Backend Developer;1\n'
      + 'Frontend Developer;1\n'
      + 'Full Stack Developer;2\n'
      + 'Senior Developer;3\n'
      + 'Tech Lead;4';

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'plantilla_roles.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Carga Masiva de Roles de Proyecto</Typography>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Box mb={3}>
          <Typography variant="body2" color="textSecondary" paragraph>
            Cargue un archivo CSV con los roles de proyecto a crear.
            El archivo debe contener las siguientes columnas:
          </Typography>
          <Typography variant="body2" component="div" sx={{ pl: 2 }}>
            <ul style={{ margin: 0 }}>
              <li><strong>name:</strong> Nombre del rol (ej: &quot;Tech Lead&quot;)</li>
              <li><strong>minimumLevel:</strong> Nivel mínimo requerido (número entero, ej: 2)</li>
            </ul>
          </Typography>
        </Box>

        <Box mb={2}>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={downloadTemplate}
            fullWidth
          >
            Descargar Plantilla CSV
          </Button>
        </Box>

        <Box mb={2}>
          <input
            accept=".csv"
            style={{ display: 'none' }}
            id="csv-file-upload-roles"
            type="file"
            onChange={handleFileChange}
          />
          <label htmlFor="csv-file-upload-roles">
            <Button
              variant="contained"
              component="span"
              startIcon={<CloudUploadIcon />}
              fullWidth
            >
              Seleccionar Archivo CSV
            </Button>
          </label>
        </Box>

        {file && (
          <Box mb={2}>
            <Alert severity="info">
              Archivo seleccionado: <strong>{file.name}</strong>
            </Alert>
          </Box>
        )}

        {uploading && (
          <Box mb={2}>
            <LinearProgress />
            <Typography variant="body2" align="center" sx={{ mt: 1 }}>
              Cargando roles...
            </Typography>
          </Box>
        )}

        {uploadResult && (
          <Box mb={2}>
            <Alert severity={uploadResult.success ? 'success' : 'error'}>
              <Typography variant="body2">
                {uploadResult.message}
              </Typography>
              {uploadResult.created > 0 && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Roles creados: <strong>{uploadResult.created}</strong>
                  {uploadResult.totalRows && uploadResult.totalRows > uploadResult.created && (
                    <> de <strong>{uploadResult.totalRows}</strong> filas procesadas</>
                  )}
                </Typography>
              )}
              {uploadResult.errors && uploadResult.errors.length > 0 && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="body2" color="error">
                    Errores encontrados:
                  </Typography>
                  <ul style={{ margin: '4px 0', paddingLeft: '20px' }}>
                    {uploadResult.errors.slice(0, 5).map((error, index) => (
                      <li key={index}>
                        <Typography variant="caption" color="error">
                          {error}
                        </Typography>
                      </li>
                    ))}
                  </ul>
                  {uploadResult.errors.length > 5 && (
                    <Typography variant="caption" color="error">
                      ... y {uploadResult.errors.length - 5} errores más
                    </Typography>
                  )}
                </Box>
              )}
            </Alert>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} color="secondary">
          Cerrar
        </Button>
        <Button
          onClick={handleUpload}
          color="primary"
          variant="contained"
          disabled={!file || uploading}
          startIcon={<CloudUploadIcon />}
        >
          Cargar Roles
        </Button>
      </DialogActions>
    </Dialog>
  );
}

BulkUploadRolesModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
};

BulkUploadRolesModal.defaultProps = {
  onSuccess: null,
};

export default BulkUploadRolesModal;
